import { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import saveFile from "../../../hooks/SaveFile";
import useInterviewProcessor from "../useInterviewProcessor";
import PrivacyStepsProgress from "../PrivacyStepsProgress";
import TranscriptReview from "../TranscriptReview";

// AI-CONTEXT: See docs/features/storytelling-document-upload.md

const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];

// Mirrors MAX_FILE_BYTES in functions/privacy/extractDocumentText.js — the
// server rejects larger files anyway; this just fails before the upload.
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const hasAcceptedExtension = (name) =>
  ACCEPTED_EXTENSIONS.some((ext) => (name || "").toLowerCase().endsWith(ext));

/**
 * Document variant of AudioUploadTab: upload one written interview (PDF or
 * .docx), run it through the privacy pipeline (extract → redact → detect),
 * then review and save exactly like the audio flow.
 *
 * @param {object} props
 * @param {object} props.interview - The StorytellingInterviews doc being processed
 * @param {(phase: string) => void} props.onPhaseChange - Reports the processing phase up so the detail page can hide the tab chooser
 * @param {(result: object) => void} props.onAnalysisComplete - Called with the full analysis (quotes, anecdotes, TOC couplings, evidence, tags, summary) for review after the AI analysis finishes
 * @param {(message: string) => void} props.onAnalysisError - Called with a message when the AI analysis fails
 */
const DocumentUploadTab = ({ interview, onPhaseChange, onAnalysisComplete, onAnalysisError }) => {
  const [documentFile, setDocumentFile] = useState(/** @type {File | null} */ (null));
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);

  const { privacySteps, phase, error, transcript, setTranscript, documentMeta, runPrivacy, save } =
    useInterviewProcessor(interview, { mode: "document" });

  // Let the detail page know when we leave the idle state (uploading counts
  // too) so it can hide the heading and tab buttons.
  useEffect(() => {
    onPhaseChange?.(uploading ? "processing" : phase);
  }, [uploading, phase, onPhaseChange]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!hasAcceptedExtension(file.name)) {
      setFileError(
        "Dit bestandstype wordt niet ondersteund. Upload een PDF- of Word-bestand (.docx)."
      );
      setDocumentFile(null);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("Dit bestand is te groot (maximaal 20 MB).");
      setDocumentFile(null);
      return;
    }
    setFileError("");
    setDocumentFile(file);
  };

  const uploadDocument = (file) =>
    new Promise((resolve, reject) => {
      // saveFile stores under file.name verbatim — prefix a uuid so two
      // uploads named "interview.pdf" never collide in Storage.
      const renamed = new File([file], `${uuid()}_${file.name}`, { type: file.type });
      saveFile({ target: { files: [renamed] } }, resolve, undefined, undefined, reject);
    });

  const handleProcess = async () => {
    if (!documentFile) return;

    setUploading(true);
    let url;
    try {
      url = await uploadDocument(documentFile);
    } catch (err) {
      console.error(err);
      setFileError("Uploaden is mislukt. Probeer het opnieuw.");
      setUploading(false);
      return;
    }

    setUploading(false);
    await runPrivacy(url);
  };

  // The transcript is persisted at the start of save(), which unmounts this
  // tab. So we report the analysis result up to the detail page (which stays
  // mounted) instead of rendering it here.
  const handleSave = async () => {
    try {
      const result = await save();
      // Hand up the full analysis (quotes, anecdotes, TOC couplings, evidence,
      // tags, summary) — the detail page reviews it all as one proposal set.
      onAnalysisComplete?.(result);
    } catch (err) {
      onAnalysisError?.(err.message);
    }
  };

  return (
    <div className="audio-recorder-container">
      {phase === "idle" && !uploading && (
        <>
          <input type="file" accept=".pdf,.docx" onChange={handleFileChange} />
          {documentFile && (
            <p className="document-upload-filename">{documentFile.name}</p>
          )}
          {documentFile && <button onClick={handleProcess}>Verwerken</button>}
        </>
      )}

      {uploading && <p className="privacy-steps-finalizing">Document uploaden…</p>}
      {phase === "processing" && <PrivacyStepsProgress steps={privacySteps} />}

      {phase === "review" && (
        <>
          {documentMeta?.truncated && (
            <p className="document-upload-truncated">
              Het document is langer dan 50.000 tekens — alleen het eerste deel is
              verwerkt en staat hieronder.
            </p>
          )}
          <TranscriptReview
            transcript={transcript}
            setTranscript={setTranscript}
            onSave={handleSave}
          />
        </>
      )}

      {phase === "saving" && (
        <p className="privacy-steps-finalizing">Transcriptie opslaan…</p>
      )}

      {(error || fileError) && (
        <p className="document-upload-error">{error || fileError}</p>
      )}
    </div>
  );
};

export default DocumentUploadTab;
